import { duration } from '@play-co/components';
import StateObserver from 'src/StateObserver';
import { useCache } from 'src/lib/utils';
import * as Visit from 'src/replicant/visit';

export function searchNonFriendPlayers(searchStr: string) {
  return useCache(
    async () => {
      return StateObserver.replicant.asyncGetters.searchNonFriendPlayers({
        searchStr,
      });
    },
    `searchNonFriendPlayers-${searchStr}`,
    duration({ minutes: 10 }),
  );
}

export function searchNonFriendPlayerEntries(searchStr: string) {
  return useCache(
    async () => {
      return StateObserver.replicant.asyncGetters.searchNonFriendPlayerEntries({
        searchStr,
      });
    },
    `searchNonFriendPlayerEntries-${searchStr}`,
    duration({ minutes: 10 }),
  );
}

export function fetchFriendList() {
  return useCache(
    async () => {
      return StateObserver.replicant.asyncGetters.fetchFriendList({});
    },
    `fetchFriendList`,
    duration({ minutes: 10 }),
  );
}

/**
 * Remove a friend from the cached friend list without refetching.
 * This way we can remove a friend immediately after unfriending without invalidating the whole cache and refetching the list,
 * which would be unnecessary since we already know the result and would cause extra loading time and server requests.
 */
export function removeFriendFromCache(friendId: string) {
  fetchFriendList().setCached((cached) => {
    if (!cached) return undefined;
    cached.data = cached.data.filter((friend) => friend.id !== friendId);
    return cached;
  });
}

export function fetchFriendSuggestions() {
  return useCache(
    async () => {
      return StateObserver.replicant.asyncGetters.fetchFriendSuggestions({});
    },
    `fetchFriendSuggestions`,
    duration({ minutes: 10 }),
  );
}

/**
 * Remove a player from the cached suggestions without refetching.
 * Used after sending a friend request to a suggested player.
 */
export function removeSuggestionFromCache(playerId: string) {
  fetchFriendSuggestions().setCached((cached) => {
    if (!cached) return undefined;
    cached.data = cached.data.filter((player) => player.id !== playerId);
    return cached;
  });
}

export function fetchFullUserProfile(playerId: string) {
  return useCache(
    async () => {
      return StateObserver.replicant.asyncGetters.fetchFullUserProfile(
        playerId,
      );
    },
    `fetchFullUserProfile-${playerId}`,
    duration({ seconds: 10 }),
  );
}

export function fetchRecentVisitors() {
  return useCache(
    async () => {
      const { visitListLimit } = Visit.ruleset;
      return StateObserver.replicant.asyncGetters.getRecentVisitorList(
        visitListLimit,
      );
    },
    'fetchRecentVisitors',
    duration({ minutes: 10 }),
  );
}
